// Drone system - shared specifications between client and server
// See docs/mechanics/drones.md for full design

export type DroneType = 'miner' | 'hauler' | 'fighter';
export type DroneOwnerType = 'space_station' | 'private_station' | 'ship' | 'settlement';

// Drone runtime states (FSM)
export type DroneStateValue =
  | 'idle'
  | 'seeking'
  | 'mining'
  | 'returning'
  | 'dumping'
  | 'loading'
  | 'price_exploring'
  | 'hauling_buying'
  | 'hauling_traveling'
  | 'hauling_selling'
  | 'patrolling'
  | 'engaging'
  | 'investigating'
  | 'recalling'
  | 'rogue';

export interface DroneStats {
  hull: number;
  shield: number;
  speed: number;
  acceleration: number;       // units/s², forward/reverse thrust
  strafeAcceleration: number; // units/s², lateral maneuvering jets
  brakingPower: number;
  rotationSpeed: number;      // degrees/s, with angular acceleration like native ships
  mass: number;
  cargoCapacity: number;
  sensorRange: number;
  miningRate?: number;
  weaponDamage?: number;
  weaponRange?: number;
  weaponCooldownMs?: number;
  healRatePerSec?: number;
  // Miner-specific derived stats — only present on the result of
  // getEffectiveDroneStats() for miner drones. Drives the player-mining formula
  // (computeMiningTierMultiplier(toolTier, resourceTier) × miningPower).
  // Mapped from MINING_TOOLS[`tool_mining_t${level}`] per the drone level system.
  miningTier?: number;
  miningPower?: number;
  miningRange?: number;
}

export interface DroneSpec {
  type: DroneType;
  stats: DroneStats;
  priceCredits: number;
  rogueChancePerAction: number;
  goldDropOnDeath: number;
  dropAsItemChance: number;
}

// Baseline gold drop on drone death — single value, no longer scaled by tier.
export const GOLD_DROP_PER_DRONE: number = 150;

export const DRONE_SPECS: Record<DroneType, DroneSpec> = {
  miner: {
    type: 'miner',
    stats: {
      hull: 90,
      shield: 30,
      speed: 140, acceleration: 65, strafeAcceleration: 45, brakingPower: 110, rotationSpeed: 140,
      mass: 80,
      cargoCapacity: 30,
      sensorRange: 1200,
      miningRate: 1.5,
    },
    priceCredits: 4500,
    rogueChancePerAction: 0.003,
    goldDropOnDeath: GOLD_DROP_PER_DRONE,
    dropAsItemChance: 0.05,
  },
  hauler: {
    type: 'hauler',
    stats: {
      hull: 120,
      shield: 45,
      speed: 165, acceleration: 45, strafeAcceleration: 32, brakingPower: 88, rotationSpeed: 90,
      mass: 100,
      cargoCapacity: 75,
      sensorRange: 1500,
    },
    priceCredits: 6000,
    rogueChancePerAction: 0.003,
    goldDropOnDeath: GOLD_DROP_PER_DRONE,
    dropAsItemChance: 0.05,
  },
  fighter: {
    type: 'fighter',
    stats: {
      hull: 75,
      shield: 60,
      speed: 230, acceleration: 80, strafeAcceleration: 60, brakingPower: 140, rotationSpeed: 170,
      mass: 60,
      cargoCapacity: 0,
      sensorRange: 1800,
      weaponDamage: 8,
      weaponRange: 700,
      weaponCooldownMs: 1050,
      healRatePerSec: 7,
    },
    priceCredits: 9000,
    rogueChancePerAction: 0.005,
    goldDropOnDeath: GOLD_DROP_PER_DRONE,
    dropAsItemChance: 0.05,
  },
};

export function getDroneSpec(type: DroneType): DroneSpec {
  return DRONE_SPECS[type];
}

/**
 * Declarative per-type display metadata — single source of truth for label, colors,
 * and short code used across client surfaces (in-world sprite, map overlay dot, HUD
 * chips, debug labels) and any future ones (chat tags, backoffice). Adding a new
 * drone type means adding ONE entry here, and TypeScript's `Record<DroneType, ...>`
 * forces it at compile time.
 *
 * Two color fields by design — they serve different visual purposes and the existing
 * palettes are preserved verbatim from the pre-refactor scattered constants:
 *  - spriteColor: numeric hex used by the PixiJS in-world Drone entity (faction tint
 *    — blue miner / orange hauler / red fighter, classic conventions).
 *  - mapColor:    numeric hex used by the ConstellationMapOverlay canvas dot (differentiable
 *    status palette — green miner / cyan hauler / orange fighter).
 *
 * For CSS string contexts (canvas fillStyle, inline styles), convert via
 * `hexToCss(spriteColor)` from utils — keeps the numeric form canonical.
 */
export const DRONE_TYPE_DISPLAY: Record<DroneType, {
  label: string;
  shortCode: string;
  spriteColor: number;
  mapColor: number;
}> = {
  miner:   { label: 'Miner Drone',   shortCode: 'MNR', spriteColor: 0x88aaff, mapColor: 0x44ff88 },
  hauler:  { label: 'Hauler Drone',  shortCode: 'HLR', spriteColor: 0xffaa44, mapColor: 0x44ddff },
  fighter: { label: 'Fighter Drone', shortCode: 'FGT', spriteColor: 0xff5555, mapColor: 0xff8844 },
};

/** Convert a numeric hex color to a `#rrggbb` CSS string. */
export function hexToCss(hex: number): string {
  return '#' + (hex & 0xffffff).toString(16).padStart(6, '0');
}

// Capacity of a space station by tier (max active drones).
// Generous numbers — drones are core economic and visual content; we want them present.
export const STATION_DRONE_CAPACITY_BY_TIER: Record<1 | 2 | 3 | 4, number> = {
  1: 30,
  2: 50,
  3: 75,
  4: 100,
};

/**
 * Target composition (fractions) for the auto-replenish system.
 * A station strives to keep this mix as it grows toward its cap.
 *   miner  — produces resources, drives revenue
 *   hauler — runs arbitrage between stations, moves credits across pools
 *   fighter — defends drones and station ZoI from hostiles
 */
export const STATION_DRONE_COMPOSITION: Record<DroneType, number> = {
  miner: 0.40,
  hauler: 0.10,
  fighter: 0.50,
};

/**
 * Minimum drones a station keeps alive even when its credit pool is empty
 * ("free fallback"). Prevents stations from being permanently extinguished
 * by sustained pressure from pirates.
 */
export const STATION_FREE_FALLBACK: Record<DroneType, number> = {
  miner: 2,
  hauler: 0,
  fighter: 2,
};

/** Initial seed credits for every NPC station's synthetic player at boot. */
export const STATION_INITIAL_CREDITS: number = 15_000;

/** How often the auto-replenish loop evaluates each station (ms). */
export const STATION_REPLENISH_TICK_MS: number = 5 * 60 * 1000;

// Fighter zone of influence (ZoI) radius by station tier.
// MUST stay above STATION_ZONES.SAFE_ZONE_RADIUS (2000): players can't be damaged inside the
// safe zone, so a ZoI ≤ 2000 leaves a defender with no ring in which it can ever engage a
// player. These values give each tier a real combat ring beyond the safe-zone bubble.
export const FIGHTER_ZOI_BY_STATION_TIER: Record<1 | 2 | 3 | 4, number> = {
  1: 4000,
  2: 5000,
  3: 6500,
  4: 8000,
};

// Behavior tuning
export const DRONE_CONFIG = {
  // How often the AI ticks per drone (ms)
  AI_TICK_INTERVAL_MS: 200,
  // Distance a drone is considered "arrived" at a target
  ARRIVAL_DISTANCE: 80,
  // Mining range (drone to asteroid surface)
  MINING_RANGE: 250,
  // Idle wait when nothing to do (ms)
  IDLE_WAIT_MS: 30_000,
  // Time after engagement ends before patrol resumes (ms)
  POST_ENGAGE_COOLDOWN_MS: 2_000,
  // Hull % to bail back to station
  RETREAT_HULL_THRESHOLD: 0.3,
  // Aggressor-against-station memory window (ms): players who shot a station drone
  AGGRESSOR_MEMORY_MS: 30_000,
  // Salvage item id prefix when a drone drops as item
  SALVAGE_ITEM_PREFIX: 'drone_salvage',
} as const;

// Build the salvage item id (used in drops when a drone is recovered).
// Single SKU per role — no longer tier-suffixed.
export function salvageItemId(type: DroneType): string {
  return `${DRONE_CONFIG.SALVAGE_ITEM_PREFIX}_${type}`;
}

// Salvage items are revendible as resources at the marketmaker.
// Single baseline price per role (used to be tier-scaled).
const SALVAGE_PRICE_PER_ROLE: number = 300;

/**
 * Map of salvage item id → base price. Consumed by MarketMakerService through
 * getResourceBasePriceIncludingSalvage(). NOT exposed to the alpha-liquidity bot,
 * which would otherwise spam standing sell orders for items nobody can produce
 * except by killing drones.
 */
export const SALVAGE_BASE_PRICES: Record<string, number> = {
  [salvageItemId('miner')]: SALVAGE_PRICE_PER_ROLE,
  [salvageItemId('hauler')]: SALVAGE_PRICE_PER_ROLE,
  [salvageItemId('fighter')]: SALVAGE_PRICE_PER_ROLE,
};

export function getSalvageBasePrice(itemId: string): number {
  return SALVAGE_BASE_PRICES[itemId] ?? 0;
}
